"use client";
import Link from "next/link";
import { useState } from "react";
import { Bookmark, Check } from "lucide-react";
import { accountRequest, AccountClientError } from "@/lib/customy/account-client";
import type { CategoryId } from "@/lib/comparison-catalog";
import { trackAnalytics } from "@/lib/customy/analytics-client";
import type { Locale } from "@/lib/i18n/config";
import { localePath } from "@/lib/i18n/routes";

/** English wording of AccountClientError (whose own message is the Spanish one). */
const clientErrorEn = (status: number) => status === 401 ? "Sign in to save your research."
  : status === 403 ? "Verify your email before continuing." : status === 404 ? "This option is no longer available in the catalog."
    : "We couldn't reach your account. Please try again.";

/** Brazilian Portuguese wording of AccountClientError. */
const clientErrorPt = (status: number) => status === 401 ? "Entre para salvar sua pesquisa."
  : status === 403 ? "Verifique seu e-mail antes de continuar." : status === 404 ? "Esta opção não está mais disponível no catálogo."
    : "Não conseguimos acessar sua conta. Tente novamente.";

/** The user-facing message of an account API error, in `locale`. */
export function accountClientMessage(error: AccountClientError, locale: Locale) {
  return locale === "en" ? clientErrorEn(error.status) : locale === "pt" ? clientErrorPt(error.status) : error.message;
}

const es = {
  saved: "Guardado en tu cuenta.",
  failed: "No pudimos guardar. Inténtalo de nuevo.",
  saving: "Guardando…",
  done: "Guardado",
  saveComparison: "Guardar comparación",
  saveFavorite: "Guardar favorito",
  goToAccount: "Ir a mi cuenta →",
};
const en: typeof es = {
  saved: "Saved to your account.",
  failed: "We couldn't save this. Please try again.",
  saving: "Saving…",
  done: "Saved",
  saveComparison: "Save comparison",
  saveFavorite: "Save to favorites",
  goToAccount: "Go to my account →",
};
const pt: typeof es = {
  saved: "Salvo na sua conta.",
  failed: "Não conseguimos salvar. Tente novamente.",
  saving: "Salvando…",
  done: "Salvo",
  saveComparison: "Salvar comparação",
  saveFavorite: "Salvar nos favoritos",
  goToAccount: "Ir para minha conta →",
};
const copy = { es, en, pt };

type Props = { category: CategoryId; locale?: Locale } & ({ productId: string } | { items: string[]; title: string });
export function AccountSave(props: Props) {
  const locale = props.locale ?? "es", text = copy[locale];
  const comparison = "items" in props;
  const [busy, setBusy] = useState(false), [saved, setSaved] = useState(false), [message, setMessage] = useState(""), [needsAccount, setNeedsAccount] = useState(false);
  return <div className="vc-account"><button className="ui-btn ui-btn-outline" type="button" disabled={busy || saved} onClick={async () => {
    setBusy(true); setMessage(""); setNeedsAccount(false);
    try {
      const body = comparison ? { category: props.category, items: props.items, title: props.title.slice(0, 120) }
        : { category: props.category, productId: props.productId };
      await accountRequest(comparison ? "comparisons" : "favorites", "PUT", body); setSaved(true); setMessage(text.saved);
      void trackAnalytics(comparison ? "codifly.comparison_saved" : "codifly.favorite_saved", { pageType: comparison ? "comparison" : "product", category: props.category, productIds: comparison ? props.items : [props.productId] });
    } catch (error) { setMessage(error instanceof AccountClientError ? accountClientMessage(error, locale) : text.failed);
      setNeedsAccount(error instanceof AccountClientError && [401, 403].includes(error.status)); }
    finally { setBusy(false); }
  }}>{saved ? <Check size={14} /> : <Bookmark size={14} />}{busy ? text.saving : saved ? text.done : comparison ? text.saveComparison : text.saveFavorite}</button>
    <span role="status">{message} {(needsAccount || saved) && <Link href={localePath(locale, "/account")}>{text.goToAccount}</Link>}</span></div>;
}
